import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users,
  Upload,
  FileBarChart,
  ShieldCheck,
  BadgeCheck,
  AlertTriangle,
  FileText,
  ArrowRight,
  Activity,
  Hash,
  Clock3,
  Sparkles,
  TrendingUp,
  ClipboardCheck,
  AlarmClock,
  CalendarClock,
  UserPlus,
  CheckCircle2,
  Lightbulb,
  Settings2,
  Radio,
  BookOpen,
  Zap,
  ChevronRight,
} from "lucide-react";
import AppShell from "../AppShell";
import { useAuth } from "../AuthContext";
import { api } from "../api";
import CompetencyLibrary from "../components/CompetencyLibrary";
import { ErrorBanner } from "../../components/primitives";

/* -------- workspace tuning knobs (unchanged) -------- */
const CERT_EXPIRY_MONTHS = 12;
const OVERDUE_ASSIGNMENT_DAYS = 14;
const STALE_DOC_MONTHS = 12;

export default function Dashboard() {
  const { business, user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [certs, setCerts] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [team, setTeam] = useState([]);
  const [docs, setDocs] = useState([]);
  const [draftModules, setDraftModules] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/compliance/summary"),
      api.get("/certificates"),
      api.get("/assignments"),
      api.get("/team"),
      api.get("/documents"),
      api.get("/learning-modules?status=draft"),
    ])
      .then(([s, c, a, t, d, drafts]) => {
        if (!alive) return;
        setSummary(s);
        setCerts(c);
        setAssignments(a);
        setTeam(t);
        setDocs(d);
        setDraftModules(drafts || []);
      })
      .catch((e) => alive && setError(e.message));
    return () => {
      alive = false;
    };
  }, []);

  const s = summary || {};
  const score = s.compliance_score ?? 0;
  const outstanding = s.assignments_outstanding ?? 0;

  const feed = useMemo(() => buildFeed({ certs, assignments }), [certs, assignments]);
  const trained = team.filter((m) => m.training_status === "Fully trained");
  const inProgress = team.filter((m) => m.training_status === "In progress");
  const notStarted = team.filter(
    (m) => m.training_status !== "Fully trained" && m.training_status !== "In progress"
  );

  const now = Date.now();
  const overdueAssignments = useMemo(
    () =>
      assignments
        .filter(
          (a) =>
            a.status !== "completed" &&
            a.assigned_at &&
            (now - new Date(a.assigned_at).getTime()) / 86400000 > OVERDUE_ASSIGNMENT_DAYS
        )
        .sort((a, b) => new Date(a.assigned_at) - new Date(b.assigned_at)),
    [assignments, now]
  );
  const expiringCerts = useMemo(() => {
    const soonMs = 30 * 86400000;
    return certs
      .map((c) => {
        const issued = c.completed_at ? new Date(c.completed_at).getTime() : null;
        const expiresAt = issued ? issued + CERT_EXPIRY_MONTHS * 30 * 86400000 : null;
        return { ...c, _expiresAt: expiresAt };
      })
      .filter((c) => c._expiresAt && c._expiresAt - now < soonMs && c._expiresAt - now > -soonMs)
      .sort((a, b) => a._expiresAt - b._expiresAt);
  }, [certs, now]);
  const staleDocs = useMemo(
    () =>
      docs
        .filter(
          (d) =>
            d.created_at &&
            (now - new Date(d.created_at).getTime()) / 86400000 >
              STALE_DOC_MONTHS * 30
        )
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at)),
    [docs, now]
  );

  // Priority queue — merges attention + rules into a single ranked action list.
  const priorityQueue = useMemo(() => {
    const items = [];
    if (draftModules.length > 0) {
      items.push({
        key: "attention-drafts",
        priority: 1,
        icon: ClipboardCheck,
        tone: "gold",
        count: draftModules.length,
        title: "Learning drafts awaiting review",
        detail: `${draftModules.length} ${draftModules.length === 1 ? "module" : "modules"} · staff can't be assessed until you approve them`,
        to: "/app/learning-review",
        cta: "Review drafts",
        examples: draftModules.slice(0, 2).map(
          (m) => m.title || m.competency_name || "Untitled module"
        ),
      });
    }
    if (overdueAssignments.length > 0) {
      items.push({
        key: "attention-overdue",
        priority: 2,
        icon: AlarmClock,
        tone: "amber",
        count: overdueAssignments.length,
        title: "Overdue training",
        detail: `${overdueAssignments.length} ${overdueAssignments.length === 1 ? "person is" : "people are"} more than ${OVERDUE_ASSIGNMENT_DAYS} days past their assignment`,
        to: "/app/team",
        cta: "Send nudge",
        examples: overdueAssignments.slice(0, 2).map((a) => a.team_member_name || "Team member"),
      });
    }
    if (expiringCerts.length > 0) {
      items.push({
        key: "attention-expiring",
        priority: 3,
        icon: CalendarClock,
        tone: "rose",
        count: expiringCerts.length,
        title: "Certificates expiring soon",
        detail: `${expiringCerts.length} within 30 days · plan renewals now to stay inspection-ready`,
        to: "/app/training",
        cta: "Plan renewals",
        examples: expiringCerts.slice(0, 2).map((c) => c.team_member_name || "Certificate"),
      });
    }
    if (staleDocs.length > 0) {
      items.push({
        key: "attention-stale",
        priority: 4,
        icon: FileText,
        tone: "slate",
        count: staleDocs.length,
        title: "Documents to revise",
        detail: `Unreviewed for more than ${STALE_DOC_MONTHS} months — pushing a revision resets assessments across affected staff`,
        to: "/app/documents",
        cta: "Open Documents",
        examples: staleDocs.slice(0, 2).map((d) => d.title),
      });
    }
    if (team.length === 0) {
      items.push({
        key: "rec-team",
        priority: 5,
        icon: UserPlus,
        tone: "gold",
        count: 0,
        title: "Invite your first team member",
        detail: "Add someone so you can start assigning training and building your evidence trail.",
        to: "/app/team",
        cta: "Add team member",
      });
    }
    if (docs.length === 0) {
      items.push({
        key: "rec-docs",
        priority: 6,
        icon: Upload,
        tone: "gold",
        count: 0,
        title: "Upload your first SOP",
        detail: "Drop in a PDF to get an AI-drafted learning module in about a minute.",
        to: "/app/documents",
        cta: "Upload SOP",
      });
    }
    return items.slice(0, 5);
  }, [
    draftModules,
    overdueAssignments,
    expiringCerts,
    staleDocs,
    team.length,
    docs.length,
  ]);

  const attentionCount =
    draftModules.length +
    overdueAssignments.length +
    expiringCerts.length +
    staleDocs.length;

  const greeting = greetingByHour();
  const firstName = ((user?.name || "").split(" ")[0] || "").trim();

  return (
    <AppShell>
      {/* ============================================================
          Command header — compact salutation + trust chips
          ============================================================ */}
      <header
        className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4"
        data-testid="dashboard-header"
      >
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            {business?.name || "Your business"} · Control centre
          </div>
          <h1 className="mt-2 font-display text-3xl md:text-[38px] tracking-tight text-slate-900 font-semibold leading-[1.1]">
            {greeting}
            {firstName ? `, ${firstName}` : ""}.
          </h1>
          <p className="mt-2 text-slate-500 text-[15px] max-w-2xl leading-relaxed">
            {attentionCount === 0
              ? "You're all clear. Every draft is reviewed, every assignment is on track, every certificate is current."
              : `${attentionCount} ${attentionCount === 1 ? "item needs" : "items need"} your attention today.`}
          </p>
        </div>
        <div className="flex items-center gap-2.5 flex-wrap">
          <TrustChip icon={Hash} label="SHA-256" />
          <TrustChip icon={ShieldCheck} label="Retained 7y" tone="emerald" />
          <Link
            to="/app/admin"
            data-testid="dashboard-admin-link"
            className="inline-flex items-center gap-1.5 rounded-full border border-[#D4A65A]/40 bg-[#FDF6E7] px-3 py-1 text-[10.5px] uppercase tracking-[0.16em] font-semibold text-[#8C6B33] hover:bg-[#F0E5C6] transition-colors"
          >
            <Settings2 size={11} /> Administration
          </Link>
        </div>
      </header>

      <ErrorBanner
        error={error}
        title="Could not load your workspace summary"
        className="mb-6"
      />

      {/* ============================================================
          Zone 1 · Compliance hero + KPI rail
          ============================================================ */}
      <section
        className="mb-8 grid lg:grid-cols-[1.4fr,1fr] gap-5"
        data-testid="dashboard-summary"
      >
        <ComplianceHero
          score={score}
          outstanding={outstanding}
          summary={s}
        />
        <KpiRail summary={s} draftCount={draftModules.length} outstanding={outstanding} />
      </section>

      {/* ============================================================
          Zone 2 · Priority queue + Live activity
          ============================================================ */}
      <section className="mb-10 grid lg:grid-cols-[1.55fr,1fr] gap-5">
        <PriorityQueue
          items={priorityQueue}
          allClear={priorityQueue.length === 0}
        />
        <ActivityFeed feed={feed} />
      </section>

      {/* ============================================================
          Zone 3 · Operations grid (owner shortcuts)
          ============================================================ */}
      <section className="mb-10" data-testid="dashboard-operations">
        <SectionKicker
          label="Operations"
          hint="Jump straight into the workflow you need"
          icon={Zap}
        />
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <OperationCard
            testid="op-team"
            to="/app/team"
            icon={Users}
            title="Team"
            body="Invite, assign roles, send reminders."
            meta={`${team.length} member${team.length === 1 ? "" : "s"}`}
          />
          <OperationCard
            testid="op-documents"
            to="/app/documents"
            icon={Upload}
            title="Documents"
            body="Upload SOPs, revise versions, assign audiences."
            meta={`${docs.length} on file`}
            highlight
          />
          <OperationCard
            testid="op-learning"
            to="/app/learning-review"
            icon={BookOpen}
            title="Learning review"
            body="Approve AI-drafted training and assessments."
            meta={draftModules.length > 0 ? `${draftModules.length} pending` : "All caught up"}
            metaTone={draftModules.length > 0 ? "amber" : "emerald"}
          />
          <OperationCard
            testid="op-training"
            to="/app/training"
            icon={BadgeCheck}
            title="Training centre"
            body="View certificates, replay assessments, download PDFs."
            meta={`${s.certificates_issued ?? 0} issued`}
          />
          <OperationCard
            testid="op-reports"
            to="/app/reports"
            icon={FileBarChart}
            title="Reports"
            body="Generate an inspection-ready PDF in one click."
            meta="Signed · SHA-256"
          />
          <OperationCard
            testid="op-admin"
            to="/app/admin"
            icon={Settings2}
            title="Administration"
            body="Organisation, users, evidence trail and system."
            meta="Owner only"
            metaTone="gold"
          />
        </div>
      </section>

      {/* ============================================================
          Zone 4 · Team health + Recent evidence
          ============================================================ */}
      <div className="grid lg:grid-cols-3 gap-5 mb-10">
        <div
          className="rounded-2xl border border-slate-200 bg-white p-6"
          data-testid="dashboard-team-health"
        >
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#8C6B33] font-semibold">
            <Users size={12} /> Team health
          </div>
          <div className="mt-4 space-y-3.5">
            <TeamHealthRow
              tone="emerald"
              label="Fully trained"
              count={trained.length}
              total={team.length}
            />
            <TeamHealthRow
              tone="gold"
              label="In progress"
              count={inProgress.length}
              total={team.length}
            />
            <TeamHealthRow
              tone="slate"
              label="Not started"
              count={notStarted.length}
              total={team.length}
            />
          </div>
          <Link
            to="/app/team"
            data-testid="dashboard-team-goto"
            className="mt-5 inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#0B1220] hover:text-[#8C6B33] transition-colors"
          >
            Manage team <ArrowRight size={12} />
          </Link>
        </div>

        {certs.length > 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.55 }}
            className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-6"
            data-testid="dashboard-recent-certs"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-700 font-semibold inline-flex items-center gap-2">
                  <BadgeCheck size={12} /> Recent evidence
                </div>
                <div className="mt-1 text-slate-500 text-[13px]">
                  The last six signed records added to your immutable audit trail.
                </div>
              </div>
              <Link
                to="/app/training"
                data-testid="dashboard-certs-view-all"
                className="text-[12px] font-semibold text-[#0B1220] hover:text-[#8C6B33] inline-flex items-center gap-1.5"
              >
                View all <ArrowRight size={12} />
              </Link>
            </div>
            <ul className="mt-4 grid sm:grid-cols-2 gap-3">
              {certs.slice(0, 6).map((c) => (
                <li
                  key={c.cert_id}
                  className="rounded-xl border border-slate-200 bg-slate-50/40 hover:bg-white transition-colors p-3.5 flex items-start gap-3"
                >
                  <span className="h-9 w-9 rounded-md bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
                    <BadgeCheck size={14} className="text-emerald-600" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-slate-900 text-[13.5px] font-semibold tracking-tight truncate">
                      {c.team_member_name}
                    </div>
                    <div className="text-[11.5px] text-slate-500 truncate">{c.module}</div>
                    <div className="text-[10px] font-mono text-slate-400 mt-1 truncate">
                      {c.cert_id}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </motion.div>
        ) : (
          <div className="lg:col-span-2 rounded-2xl border border-dashed border-slate-300 bg-white p-8 flex items-center justify-center text-center">
            <div>
              <span className="mx-auto h-11 w-11 rounded-lg bg-[#0B1220] text-[#D4A65A] flex items-center justify-center">
                <BadgeCheck size={18} />
              </span>
              <div className="mt-3 font-display text-lg text-slate-900 font-semibold">
                No certificates yet
              </div>
              <p className="mt-1 text-[13px] text-slate-500 max-w-sm mx-auto">
                Signed records appear here the moment a team member completes their first assessment.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* ============================================================
          Zone 5 · Competency Library (preserved)
          ============================================================ */}
      <CompetencyLibrary teamSize={team.length} />
    </AppShell>
  );
}

/* ============================================================
 *  Compliance hero (score + narrative + KPI mini-strip)
 * ============================================================ */
function ComplianceHero({ score, outstanding, summary }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55 }}
      className="rounded-2xl bg-gradient-to-br from-[#0B1220] via-[#0F1730] to-[#131c31] text-white p-6 md:p-8 shadow-[0_25px_60px_-25px_rgba(11,18,32,0.7)] relative overflow-hidden"
    >
      <div className="pointer-events-none absolute -top-24 -right-16 h-72 w-72 rounded-full bg-emerald-500/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-28 -left-16 h-72 w-72 rounded-full bg-[#D4A65A]/15 blur-3xl" />
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.08]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.4) 1px, transparent 0)",
          backgroundSize: "22px 22px",
        }}
      />

      <div className="relative">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4A65A] font-semibold">
            Compliance overview
          </div>
          <div className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] border border-white/10 px-2.5 py-1 text-[10px] text-slate-300 uppercase tracking-[0.16em]">
            <TrendingUp size={10} className="text-emerald-300" />
            Up to the minute
          </div>
        </div>

        <div className="mt-5 flex flex-col sm:flex-row sm:items-end gap-5">
          <ScoreRing score={score} />
          <div className="flex-1">
            <div className="text-[13.5px] text-white">
              Your business is{" "}
              <span className="text-[#D4A65A] font-semibold">{scoreLabel(score)}</span>
            </div>
            <div className="text-[12px] text-slate-400 mt-1 max-w-sm leading-relaxed">
              Based on how many of your assigned SOPs and policies have been
              acknowledged across the team.
            </div>
            <div className="mt-4 h-2 rounded-full bg-white/[0.08] overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-[#D4A65A] via-[#F0D497] to-emerald-400"
                initial={{ width: 0 }}
                animate={{ width: `${score}%` }}
                transition={{ duration: 1.1, ease: "easeOut", delay: 0.2 }}
              />
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat
            icon={Users}
            label="Staff trained"
            value={`${summary.staff_fully_trained ?? 0} / ${summary.staff_total ?? 0}`}
            testid="stat-staff"
          />
          <Stat
            icon={FileText}
            label="Documents"
            value={summary.documents_total ?? 0}
            testid="stat-docs"
          />
          <Stat
            icon={BadgeCheck}
            label="Certificates"
            value={summary.certificates_issued ?? 0}
            testid="stat-certs"
            tone="emerald"
          />
          <Stat
            icon={AlertTriangle}
            label="Outstanding"
            value={outstanding}
            tone={outstanding > 0 ? "warn" : "ok"}
            testid="stat-outstanding"
          />
        </div>
      </div>
    </motion.div>
  );
}

/* ============================================================
 *  KPI rail — light card, complements the dark hero
 * ============================================================ */
function KpiRail({ summary, draftCount, outstanding }) {
  const s = summary || {};
  const completed = s.assignments_completed ?? 0;
  const total = s.assignments_total ?? 0;
  const acceptanceRate = total > 0 ? Math.round((completed / total) * 100) : 100;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 0.08 }}
      className="rounded-2xl bg-white border border-slate-200 shadow-[0_10px_30px_-20px_rgba(11,18,32,0.15)] p-6"
      data-testid="dashboard-kpi-rail"
    >
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#8C6B33] font-semibold">
          <Radio size={12} />
          Workspace pulse
        </div>
        <span className="inline-flex items-center gap-1.5 text-[10px] text-emerald-600">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
          streaming
        </span>
      </div>
      <ul className="mt-4 space-y-2.5">
        <KpiLine
          label="Documents on file"
          value={s.documents_total ?? 0}
          testid="kpi-docs"
        />
        <KpiLine
          label="Certificates issued"
          value={s.certificates_issued ?? 0}
          testid="kpi-certs"
          tone="emerald"
        />
        <KpiLine
          label="Assignments completed"
          value={`${completed} / ${total}`}
          testid="kpi-assignments"
          hint={`${acceptanceRate}% acceptance`}
        />
        <KpiLine
          label="Drafts pending review"
          value={draftCount}
          testid="kpi-drafts"
          tone={draftCount > 0 ? "amber" : "emerald"}
        />
        <KpiLine
          label="Outstanding actions"
          value={outstanding}
          testid="kpi-outstanding"
          tone={outstanding > 0 ? "amber" : "emerald"}
        />
      </ul>
    </motion.div>
  );
}

function KpiLine({ label, value, hint, tone, testid }) {
  const valueTone =
    tone === "emerald"
      ? "text-emerald-700"
      : tone === "amber"
      ? "text-amber-700"
      : "text-slate-900";
  return (
    <li
      data-testid={testid}
      className="flex items-baseline justify-between gap-3 py-2 border-b border-slate-100 last:border-0"
    >
      <span className="text-[12px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
        {label}
      </span>
      <span className="text-right">
        <span className={`font-display text-[18px] font-semibold tracking-tight ${valueTone} tabular-nums`}>
          {value}
        </span>
        {hint && <div className="text-[10.5px] text-slate-400 mt-0.5">{hint}</div>}
      </span>
    </li>
  );
}

/* ============================================================
 *  Priority queue — merged attention + recommendations
 * ============================================================ */
function PriorityQueue({ items, allClear }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="dashboard-priority-queue">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#8C6B33] font-semibold">
          <Lightbulb size={12} />
          Priority queue
        </div>
        <span className="text-[11px] text-slate-400">
          {allClear ? "You're all clear" : `${items.length} action${items.length === 1 ? "" : "s"}`}
        </span>
      </div>

      {allClear ? (
        <div
          data-testid="dashboard-attention-clear"
          className="mt-5 rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-white p-6 flex items-start gap-4"
        >
          <span className="h-11 w-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0">
            <CheckCircle2 size={20} />
          </span>
          <div className="flex-1">
            <div className="font-display text-lg text-slate-900 font-semibold">
              You're all clear — nothing needs your attention today.
            </div>
            <p className="mt-1 text-[13.5px] text-slate-600 max-w-xl leading-relaxed">
              Every draft has been reviewed, every assignment is on track, no
              certificates are expiring within 30 days, and every document
              has been revised in the last {STALE_DOC_MONTHS} months.
            </p>
            <div className="mt-4">
              <Link
                to="/app/reports"
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#0B1220] text-white px-3.5 py-2 text-[12.5px] font-semibold hover:bg-[#131c31] transition-colors"
              >
                Run this month's compliance report <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      ) : (
        <ul className="mt-5 space-y-2.5">
          {items.map((it, idx) => (
            <PriorityRow key={it.key} item={it} index={idx + 1} />
          ))}
        </ul>
      )}
    </div>
  );
}

function PriorityRow({ item, index }) {
  const Icon = item.icon;
  const toneMap = {
    gold: { chip: "bg-[#FDF6E7] text-[#8C6B33] border-[#D4A65A]/40", accent: "text-[#8C6B33]" },
    amber: { chip: "bg-amber-50 text-amber-800 border-amber-200", accent: "text-amber-700" },
    rose: { chip: "bg-rose-50 text-rose-700 border-rose-200", accent: "text-rose-700" },
    slate: { chip: "bg-slate-100 text-slate-700 border-slate-200", accent: "text-slate-700" },
  };
  const t = toneMap[item.tone] || toneMap.slate;
  return (
    <li>
      <Link
        to={item.to}
        data-testid={item.key}
        className="group grid grid-cols-[auto,1fr,auto] items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 hover:border-[#D4A65A]/40 hover:shadow-[0_12px_28px_-18px_rgba(11,18,32,0.18)] transition-all"
      >
        <div className="flex items-center gap-3 shrink-0">
          <span className="w-6 text-center text-[10px] font-mono text-slate-400 tabular-nums">{String(index).padStart(2, "0")}</span>
          <span className={`h-9 w-9 rounded-lg border flex items-center justify-center ${t.chip}`}>
            <Icon size={14} />
          </span>
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[13.5px] text-slate-900 font-semibold tracking-tight truncate">
              {item.title}
            </div>
            {item.count > 0 && (
              <span className={`inline-flex items-center rounded-full border ${t.chip} px-1.5 py-0.5 text-[10px] font-semibold`}>
                {item.count}
              </span>
            )}
          </div>
          <div className="mt-0.5 text-[12px] text-slate-500 truncate">{item.detail}</div>
          {item.examples && item.examples.length > 0 && (
            <div className="mt-1 text-[11.5px] text-slate-400 truncate">
              {item.examples.join(" · ")}
            </div>
          )}
        </div>
        <span className={`inline-flex items-center gap-1 text-[12px] font-semibold ${t.accent} group-hover:translate-x-0.5 transition-transform`}>
          {item.cta} <ChevronRight size={13} />
        </span>
      </Link>
    </li>
  );
}

/* ============================================================
 *  Activity feed
 * ============================================================ */
function ActivityFeed({ feed }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 0.1 }}
      className="rounded-2xl bg-white border border-slate-200 shadow-[0_10px_30px_-20px_rgba(11,18,32,0.15)] p-5 flex flex-col"
      data-testid="dashboard-activity"
    >
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#8C6B33] font-semibold">
          <Activity size={12} />
          Live activity
        </div>
        <span className="inline-flex items-center gap-1.5 text-[10px] text-emerald-600">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
          streaming
        </span>
      </div>

      <ul className="mt-4 space-y-2 flex-1">
        {feed.length === 0 && (
          <li className="text-[12.5px] text-slate-500 py-8 text-center">
            No activity yet. It will appear here as your team acknowledges documents.
          </li>
        )}
        {feed.slice(0, 6).map((e, i) => (
          <motion.li
            key={e.key}
            initial={{ opacity: 0, x: 8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: i * 0.06 }}
            className="flex items-start gap-3 rounded-lg border border-slate-100 bg-slate-50/60 hover:bg-slate-50 transition-colors px-3 py-2.5"
          >
            <span
              className={`h-7 w-7 rounded-md ${
                e.tone === "emerald"
                  ? "bg-emerald-50 border-emerald-200 text-emerald-600"
                  : "bg-[#FDF6E7] border-[#D4A65A]/40 text-[#8C6B33]"
              } border flex items-center justify-center shrink-0`}
            >
              <e.icon size={13} />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] text-slate-900 truncate">
                <b>{e.who}</b>{" "}
                <span className="text-slate-500 font-normal">{e.action}</span>
              </div>
              <div className="text-[11px] text-slate-500 truncate">{e.detail}</div>
            </div>
            <span className="text-[10.5px] font-mono text-slate-400 shrink-0">
              {e.when}
            </span>
          </motion.li>
        ))}
      </ul>

      <Link
        to="/app/training"
        data-testid="dashboard-activity-goto"
        className="mt-4 inline-flex items-center justify-center gap-1.5 text-[12px] font-semibold text-[#0B1220] hover:text-[#8C6B33] transition-colors"
      >
        Open training centre <ArrowRight size={12} />
      </Link>
    </motion.div>
  );
}

/* ============================================================
 *  Operations grid
 * ============================================================ */
function OperationCard({ to, icon: Icon, title, body, meta, metaTone, highlight, testid }) {
  const metaClass =
    metaTone === "amber"
      ? "bg-amber-50 text-amber-800 border-amber-200"
      : metaTone === "emerald"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : metaTone === "gold"
      ? "bg-[#FDF6E7] text-[#8C6B33] border-[#D4A65A]/40"
      : "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`group rounded-2xl border p-5 transition-all flex flex-col relative overflow-hidden ${
        highlight
          ? "bg-white border-[#D4A65A]/40 shadow-[0_16px_40px_-20px_rgba(212,166,90,0.4)] hover:border-[#D4A65A]/70 hover:-translate-y-0.5"
          : "bg-white border-slate-200 hover:border-slate-300 hover:shadow-[0_16px_36px_-22px_rgba(11,18,32,0.25)] hover:-translate-y-0.5"
      }`}
    >
      {highlight && (
        <span className="absolute top-4 right-4 inline-flex items-center gap-1 rounded-full bg-[#D4A65A]/15 border border-[#D4A65A]/30 px-2 py-0.5 text-[9.5px] font-semibold text-[#8C6B33] uppercase tracking-[0.14em]">
          <Sparkles size={9} /> Most popular
        </span>
      )}
      <span
        className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${
          highlight ? "bg-[#D4A65A] text-[#0B1220]" : "bg-[#0B1220] text-white"
        } group-hover:scale-105 transition-transform`}
      >
        <Icon size={18} />
      </span>
      <h3 className="mt-4 text-[16px] font-display font-semibold tracking-tight text-slate-900">
        {title}
      </h3>
      <p className="mt-1.5 text-[12.5px] text-slate-500 leading-relaxed">{body}</p>
      <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
        <span className={`inline-flex items-center rounded-full border ${metaClass} px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.14em]`}>
          {meta}
        </span>
        <ArrowRight size={14} className="text-slate-400 group-hover:text-[#0B1220] group-hover:translate-x-0.5 transition-all" />
      </div>
    </Link>
  );
}

/* -------------------- helpers (preserved) -------------------- */

function SectionKicker({ label, hint, icon: Icon }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
        {Icon ? <Icon size={12} className="text-[#8C6B33]" /> : <span className="h-px w-6 bg-slate-300" />}
        {label}
      </div>
      {hint && <span className="text-[11px] text-slate-400 hidden md:inline">{hint}</span>}
    </div>
  );
}

function TeamHealthRow({ tone, label, count, total }) {
  const toneMap = {
    emerald: "bg-emerald-500",
    gold: "bg-[#D4A65A]",
    slate: "bg-slate-400",
  };
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between text-[12.5px]">
        <div className="flex items-center gap-2 text-slate-700 font-medium">
          <span className={`h-2 w-2 rounded-full ${toneMap[tone] || toneMap.slate}`} />
          {label}
        </div>
        <div className="tabular-nums text-slate-900 font-semibold">
          {count}
          <span className="text-slate-400 font-normal"> / {total}</span>
        </div>
      </div>
      <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div
          className={`h-full ${toneMap[tone] || toneMap.slate} transition-all`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function greetingByHour() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function scoreLabel(score) {
  if (score >= 95) return "inspection ready";
  if (score >= 80) return "on track";
  if (score >= 60) return "making progress";
  return "getting started";
}

function ScoreRing({ score }) {
  const size = 128;
  const stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = (score / 100) * c;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid="dashboard-score-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} fill="none" />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="url(#ringGrad)"
          strokeWidth={stroke}
          strokeLinecap="round"
          fill="none"
          initial={{ strokeDasharray: `0 ${c}` }}
          animate={{ strokeDasharray: `${dash} ${c}` }}
          transition={{ duration: 1.2, ease: "easeOut", delay: 0.15 }}
        />
        <defs>
          <linearGradient id="ringGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#D4A65A" />
            <stop offset="100%" stopColor="#34D399" />
          </linearGradient>
        </defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-display text-[38px] leading-none tracking-tight text-white font-semibold" data-testid="dashboard-score">
          {score}%
        </div>
        <div className="text-[9.5px] uppercase tracking-[0.18em] text-slate-400 mt-1">Compliant</div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tone, testid }) {
  const valueTone =
    tone === "warn" ? "text-amber-300" : tone === "emerald" ? "text-emerald-300" : tone === "ok" ? "text-emerald-300" : "text-white";
  return (
    <div className="rounded-lg bg-white/[0.05] border border-white/[0.06] p-3">
      <div className="flex items-center gap-1.5 text-[9.5px] uppercase tracking-[0.14em] text-slate-400 font-mono">
        <Icon size={10} />
        {label}
      </div>
      <div data-testid={testid} className={`mt-1 text-xl font-semibold tracking-tight ${valueTone}`}>
        {value}
      </div>
    </div>
  );
}

function TrustChip({ icon: Icon, label, tone }) {
  const cls = tone === "emerald" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-600";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border ${cls} px-3 py-1 text-[10.5px] uppercase tracking-[0.16em] font-semibold`}>
      <Icon size={11} /> {label}
    </span>
  );
}

function buildFeed({ certs, assignments }) {
  const events = [];
  for (const c of certs) {
    events.push({
      key: `c-${c.cert_id}`,
      when: fromNow(c.completed_at),
      _ts: dateVal(c.completed_at),
      who: c.team_member_name,
      action: "was certified for",
      detail: `${c.module}${c.version ? " " + c.version : ""}`,
      tone: "emerald",
      icon: BadgeCheck,
    });
  }
  for (const a of assignments) {
    if (a.status !== "completed" && a.assigned_at) {
      events.push({
        key: `a-${a.id}`,
        when: fromNow(a.assigned_at),
        _ts: dateVal(a.assigned_at),
        who: a.team_member_name || "Team",
        action: "was assigned",
        detail: `${a.document_title || "a document"} ${a.document_version || ""}`.trim(),
        tone: "gold",
        icon: Clock3,
      });
    }
  }
  return events.sort((x, y) => y._ts - x._ts);
}

function dateVal(iso) {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : 0;
}

function fromNow(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const diff = Math.max(0, Date.now() - then) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h`;
  if (diff < 604800) return `${Math.round(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}
